import {
   IFlowDraft,
   IReadinessItem,
   TRailKey,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { audienceTypes } from '@forwardflow/features/campaign-flow/utils/describe'
import { contentOf } from '@forwardflow/features/campaign-flow/utils/playbook'
import { explainReason } from '@forwardflow/features/campaign-v2/utils/config'

/** What the screens can check before asking the backend. */
export const localReadiness = (draft: IFlowDraft): IReadinessItem[] => [
   {
      key: 'who',
      /* null = the count is unavailable, which should not block a launch. */
      /* A new-leads campaign may start empty: it calls every lead that arrives from now on. */
      ok:
         draft.audience.leadCount !== 0 ||
         draft.audience.kinds.includes('new-lead'),
      label: 'Leads to call',
      fix: 'No leads match these filters yet.',
   },
   {
      key: 'who',
      /* The v2 engine has no activity trigger yet (backend change B9). */
      ok: !draft.audience.kinds.includes('activity'),
      label: 'Campaign type the engine can run',
      fix: 'Activity campaigns are not supported by the campaign engine yet. Pick New leads or Cold leads for now.',
   },
   {
      key: 'playbook',
      ok:
         draft.playbook.touches.length > 0 &&
         audienceTypes(draft.audience).every(
            (t) => contentOf(draft.playbook, t).script.length > 0
         ),
      label: 'Script, messages and timing',
      fix:
         audienceTypes(draft.audience).length > 1
            ? 'Each lead type needs a call script.'
            : undefined,
   },
   {
      key: 'voice',
      ok: !!draft.voice.optionId && !!draft.voice.name.trim(),
      label: 'A voice with a name',
      fix: 'Give the voice a name it introduces itself with.',
   },
   {
      key: 'numbers',
      ok: draft.numbers.selected.length > 0,
      label: 'Numbers to call from',
      fix: 'Pick at least one number.',
   },
   {
      key: 'handoff',
      ok: draft.handoff.agentIds.length > 0,
      label: 'Who gets the leads',
      fix: 'Choose at least one teammate.',
   },
]

/** The v2 editor's step names → this flow's rail rows. */
const STEP_TO_RAIL: Record<string, TRailKey> = {
   numbers: 'numbers',
   content: 'playbook',
   cadence: 'playbook',
   team: 'handoff',
   voice: 'voice',
}

/** A backend readiness reason in plain words, on the rail row that fixes it. */
export const reasonToItem = (reason: string): IReadinessItem => {
   const hint = explainReason(reason)
   return {
      key: STEP_TO_RAIL[hint.step] ?? 'launch',
      ok: false,
      label: hint.text,
      fix: hint.text,
   }
}
